import { SortOptions, VerifiedFilter } from "../types/leadTypes";
import { BaseStatusEnum, getDefaultStateForBaseStatus, SentStatusEnum } from "../constants/filterConstants.ts";
import { SortLeadsByOptionsEnum } from "@common/enums/AdminLeadsSortEnum.ts";
import { DisputeStatusEnum } from "@common/types/DisputeTypes.ts";

// Define the hierarchical status structure
export interface StatusHierarchy {
    mainStatus: string; // Primary level (new, pending, sent, trash)
    state: string; // Secondary level (all-new, disputed-sent, etc.)
    substates: string[]; // Third level (Pending, Approved, etc. for dispute)
}

// Define the state structure with clear hierarchy
export interface LeadsState {
    // Status-State-Substate hierarchy
    statusHierarchy: StatusHierarchy;

    // Other filters
    search: string;
    page: number;
    limit: number;
    dateRange: string[];
    counties: string[];
    categories: string[];
    transaction_status: string[];
    sortOptions: SortOptions;
    labels: string[];
    verified: boolean;
    unverified: boolean;
    duplicateGroupLeadId: string | null;
    duplicateGroupLeadName: string | null;

    // UI state
    applyFilters: boolean;
}

// Define all possible actions with hierarchy-aware operations
export type LeadsAction =
    | { type: 'SET_MAIN_STATUS'; payload: string }
    | { type: 'SET_STATE'; payload: string }
    | { type: 'SET_SUBSTATES'; payload: string[] }
    | { type: 'TOGGLE_SUBSTATE'; payload: string }
    | { type: 'SET_SEARCH'; payload: string }
    | { type: 'SET_PAGE'; payload: number }
    | { type: 'SET_LIMIT'; payload: number }
    | { type: 'SET_DATE_RANGE'; payload: string[] }
    | { type: 'SET_COUNTIES'; payload: string[] }
    | { type: 'SET_CATEGORIES'; payload: string[] }
    | { type: 'SET_SORT_OPTIONS'; payload: SortOptions }
    | { type: 'SET_LABELS'; payload: string[] }
    | { type: 'SET_VERIFIED_FILTER'; payload: VerifiedFilter }
    | { type: 'SET_DUPLICATE_GROUP'; payload: { leadId: string | null; leadName: string | null } }
    | { type: 'APPLY_FILTERS'; payload: boolean }
    | { type: 'CLEAR_FILTERS' }
    | { type: 'BATCH_UPDATE'; payload: Partial<LeadsState> };

export const ADMIN_LEADS_PAGE_SIZE_OPTIONS = [25, 50, 75, 100];
export const ADMIN_LEADS_DEFAULT_PAGE_SIZE = 100;
export const ADMIN_LEADS_PAGE_SIZE_STORAGE_KEY = 'adminLeadsPageSize';

// The chosen page size survives reloads; it is cleared on logout
export const loadAdminLeadsPageSize = (): number | null => {
    try {
        const stored = Number(localStorage.getItem(ADMIN_LEADS_PAGE_SIZE_STORAGE_KEY));
        return ADMIN_LEADS_PAGE_SIZE_OPTIONS.includes(stored) ? stored : null;
    } catch (error) {
        console.error('Error loading persisted leads page size:', error);
        return null;
    }
};

export const saveAdminLeadsPageSize = (limit: number): void => {
    try {
        localStorage.setItem(ADMIN_LEADS_PAGE_SIZE_STORAGE_KEY, String(limit));
    } catch (error) {
        console.error('Error saving leads page size:', error);
    }
};

// Initial state with proper hierarchy defaults
export const initialLeadsState: LeadsState = {
    statusHierarchy: {
        mainStatus: BaseStatusEnum.NEW,
        state: getDefaultStateForBaseStatus(BaseStatusEnum.NEW),
        substates: []
    },
    search: '',
    page: 1,
    limit: ADMIN_LEADS_DEFAULT_PAGE_SIZE,
    dateRange: ['', ''],
    counties: [],
    categories: [],
    transaction_status: [],
    sortOptions: { sortBy: SortLeadsByOptionsEnum.CREATED, order: 'asc' },
    labels: [],
    verified: true,
    unverified: true,
    duplicateGroupLeadId: null,
    duplicateGroupLeadName: null,
    applyFilters: false,
};

// Helper to determine main status from a state value
const getMainStatusFromState = (state: string): string => {
    // Check if it's a billing state first - these should always be under 'sent'
    const isBillingState = state.includes('billing');
    if (isBillingState) return BaseStatusEnum.SENT;

    // Otherwise, determine by string matching
    if (state.includes(BaseStatusEnum.NEW)) return BaseStatusEnum.NEW;
    if (state.includes(BaseStatusEnum.PENDING)) return BaseStatusEnum.PENDING;
    if (state.includes(BaseStatusEnum.SENT)) return BaseStatusEnum.SENT;
    if (state.includes(BaseStatusEnum.TRASH)) return BaseStatusEnum.TRASH;
    if (state.includes(BaseStatusEnum.ALL)) return BaseStatusEnum.ALL;
    return BaseStatusEnum.NEW; // Default
};

// Reducer function with improved hierarchy handling
export const leadsReducer = (state: LeadsState, action: LeadsAction): LeadsState => {
    let newMainStatus :string;
    let currentMainStatus :string;
    let mainStatusChanged :boolean;
    // Special handling for billing states - preserve substates
    let isBillingState :boolean;
    let currentSubstates :string[];
    let newSubstates :string[] = [];

    switch (action.type) {
        case 'SET_MAIN_STATUS':
            return {
                ...state,
                statusHierarchy: {
                    mainStatus: action.payload,
                    state: getDefaultStateForBaseStatus(action.payload),
                    substates: []
                }
            };

        case 'SET_STATE':
            // Don't unnecessarily overwrite the main status if the state corresponds to the same main status
            newMainStatus = getMainStatusFromState(action.payload);
            currentMainStatus = state.statusHierarchy.mainStatus;
            mainStatusChanged = newMainStatus !== currentMainStatus;

            // Special handling for billing states - preserve substates
            isBillingState = action.payload.includes('billing');
            currentSubstates = state.statusHierarchy.substates || [];

            // If moving to a disputed state, set default substate
            if (action.payload === SentStatusEnum.DISPUTED.toString()) {
                newSubstates = [DisputeStatusEnum.PENDING];
            } else if (isBillingState && currentSubstates.length > 0) { // If in a billing state and we had substates, preserve them
                newSubstates = currentSubstates;
            }

            return {
                ...state,
                statusHierarchy: {
                    // Preserve main status if the state is within the same category
                    mainStatus: mainStatusChanged ? newMainStatus : currentMainStatus,
                    state: action.payload,
                    substates: newSubstates
                }
            };

        case 'SET_SUBSTATES':
            return {
                ...state,
                statusHierarchy: {
                    ...state.statusHierarchy,
                    substates: action.payload
                }
            };

        case 'TOGGLE_SUBSTATE':
            return {
                ...state,
                statusHierarchy: {
                    ...state.statusHierarchy,
                    substates: state.statusHierarchy.substates.includes(action.payload)
                        ? state.statusHierarchy.substates.filter(s => s !== action.payload)
                        : [...state.statusHierarchy.substates, action.payload]
                }
            };

        case 'SET_SEARCH':
            return { ...state, search: action.payload };

        case 'SET_PAGE':
            return { ...state, page: Math.max(1, action.payload) };

        case 'SET_LIMIT':
            // A new page size always starts back on the first page
            return { ...state, limit: action.payload, page: 1 };

        case 'SET_DATE_RANGE':
            return { ...state, dateRange: action.payload };

        case 'SET_COUNTIES':
            return { ...state, counties: action.payload };

        case 'SET_CATEGORIES':
            return { ...state, categories: action.payload };

        case 'SET_SORT_OPTIONS':
            return { ...state, sortOptions: action.payload };

        case 'SET_LABELS':
            return { ...state, labels: action.payload };

        case 'SET_VERIFIED_FILTER':
            return {
                ...state,
                verified: action.payload.verified,
                unverified: action.payload.unverified
            };

        case 'SET_DUPLICATE_GROUP':
            return {
                ...state,
                duplicateGroupLeadId: action.payload.leadId,
                duplicateGroupLeadName: action.payload.leadName
            };

        case 'APPLY_FILTERS':
            // Prevent setting true again if it's already true
            if (state.applyFilters === true && action.payload === true) {
                return state;
            }
            return {
                ...state,
                applyFilters: action.payload,
            };

        case 'CLEAR_FILTERS':
            return {
                ...initialLeadsState,
                // Page size is a view preference, not a filter - keep it
                limit: state.limit,
                // Ensure verified filters are explicitly set to true
                verified: true,
                unverified: true,
                // Explicitly ensure page is reset to 1
                page: 1,
                // Reset duplicate group filter
                duplicateGroupLeadId: null,
                duplicateGroupLeadName: null,
                // And apply filters
                applyFilters: true,
            };

        case 'BATCH_UPDATE':
            return {
                ...state,
                ...action.payload,
            };

        default:
            throw new Error(`Unhandled action type: ${(action as LeadsAction).type}`);
    }
};

// Helper functions for status hierarchy management
export const statusHierarchyHelpers = {
    // Convert from hierarchy to backend API format
    toApiFormat: (hierarchy: StatusHierarchy) => ({
        status: hierarchy.state,
        dispute_status: hierarchy.state === SentStatusEnum.DISPUTED.toString() ? hierarchy.substates : [],
        transaction_status: hierarchy.state === SentStatusEnum.BILLING.toString() ? hierarchy.substates : [],
    }),

    // Convert from old format to new hierarchy format
    fromLegacyFormat: (status: string, disputeStatus: string[]) => {
        const hierarchy: StatusHierarchy = {
            mainStatus: getMainStatusFromState(status),
            state: status,
            substates: []
        };

        // If we have dispute status and are in disputed-sent state, add the substates
        if (status === SentStatusEnum.DISPUTED.toString() && disputeStatus.length > 0) {
            hierarchy.substates = disputeStatus;
        }

        return hierarchy;
    }
};
